import { toyyyyMMdd } from "#web/utils/dateFormatter";
import * as v from "valibot";

const PipeMeta = v.array(
  v.object({
    name: v.string(),
    type: v.string(),
  }),
);

const PipeRow = v.record(v.string(), v.any());
const PipeData = v.array(PipeRow);

const RowCount = v.number();

const PipeResult = v.object({
  data: PipeData,
  meta: PipeMeta,
  rows: RowCount,
});

type PipeResult = v.InferOutput<typeof PipeResult>;

interface TinybirdFetchOptions {
  [key: string]: unknown;
  endDate?: Date | string;
  shopId: string;
  startDate?: Date | string;
}

export const fetchTinybird = async (
  endpoint: string,
  { endDate, shopId, startDate, ...rest }: TinybirdFetchOptions,
) => {
  // Build parameters for the proxy request
  const params: Record<string, unknown> = {};

  if (startDate) {
    const startDateString = startDate instanceof Date ? toyyyyMMdd(startDate) : startDate;
    // Add both parameter names for compatibility with different pipes
    params["startdate"] = startDateString;
    params["start_date"] = startDateString;
  }

  if (endDate) {
    const endDateString = endDate instanceof Date ? toyyyyMMdd(endDate) : endDate;
    // Add both parameter names for compatibility with different pipes
    params["enddate"] = endDateString;
    params["end_date"] = endDateString;
  }

  // Add any additional parameters
  for (const [key, value] of Object.entries(rest)) {
    params[key] = value;
  }

  // Make request to our proxy endpoint
  const response = await fetch("/api/tinybird-proxy", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      endpoint,
      params,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    console.error("Tinybird proxy error:", error);
    throw new Error(error.message || error.error || "Tinybird data fetch error");
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || "Tinybird proxy error");
  }

  return v.parse(PipeResult, result.data);
};

export const parsePipeResult =
  <T extends v.GenericSchema>(schema: T) =>
  (result: PipeResult) => {
    const AugmentedResult = v.object({
      data: schema,
      meta: PipeMeta,
      rows: RowCount,
    });

    return v.parse(AugmentedResult, result);
  };
